/**
 * Popup Shopify installation.
 *
 * The originating Bustly tab (onboarding or Admin Integrations) stays in place
 * while Shopify App Store installation/OAuth runs in a popup window. The popup
 * hands the result back through the same-origin `connect` BroadcastChannel and
 * the originating tab resumes the existing full-page return verification.
 *
 * Each popup carries an attempt reference in its own sessionStorage; its
 * account-scoped intent lives under a per-attempt localStorage key. The opener
 * can poll for that attempt's recorded shop if callback delivery is lost.
 * Uncorrelated new tabs use the existing standalone install journey.
 */
import {
  clearOnboardingInstall,
  ONBOARDING_RETURN_KEY,
} from "./shopifyOnboardingInstall";
import { SHOPIFY_CONNECT_ATTEMPT_KEY } from "./shopifyInstall";

const STORAGE_KEY = "bustly.shopify.popup-install.v2";
const WINDOW_ATTEMPT_KEY = "bustly.shopify.popup-attempt.v1";
const TTL = 30 * 60 * 1000;
const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;
export const SHOPIFY_INSTALL_POPUP_FEATURES = "popup,width=960,height=760";
export const SHOPIFY_CONNECT_CHANNEL = "connect";

export type PopupInstallIntent = {
  accountId: string;
  productCode: string;
  workspaceId: string;
  scene: "onboarding" | "admin";
  oauthAppId: "PRIMARY" | "BACKUP";
  attemptId: string;
  createdAt: number;
  shop?: string;
};

export type PopupInstallResult =
  | { status: "success"; shop?: string }
  | { status: "error" };

/** Must run synchronously inside the user's click turn to avoid popup blockers. */
export function openShopifyInstallPopup(url = "about:blank"): Window | null {
  try {
    return (
      window.open(url, "_blank", SHOPIFY_INSTALL_POPUP_FEATURES) ||
      null
    );
  } catch {
    return null;
  }
}

export function beginPopupInstall(
  storage: Storage,
  intent: Omit<PopupInstallIntent, "createdAt" | "shop">,
  now = Date.now(),
): PopupInstallIntent {
  if (
    !intent.accountId ||
    !intent.productCode ||
    !intent.workspaceId ||
    !intent.attemptId
  )
    throw new Error("Missing popup installation scope");
  const value: PopupInstallIntent = { ...intent, createdAt: now };
  storage.setItem(`${STORAGE_KEY}:${intent.attemptId}`, JSON.stringify(value));
  return value;
}

export function readPopupInstall(
  storage: Storage,
  scope: { accountId: string; productCode: string; attemptId: string },
  now = Date.now(),
): PopupInstallIntent | null {
  try {
    if (!scope.attemptId) return null;
    const value = JSON.parse(
      storage.getItem(`${STORAGE_KEY}:${scope.attemptId}`) || "null",
    );
    if (
      !value ||
      !scope.accountId ||
      value.accountId !== scope.accountId ||
      value.productCode !== scope.productCode ||
      value.attemptId !== scope.attemptId ||
      typeof value.workspaceId !== "string" ||
      !value.workspaceId ||
      typeof value.attemptId !== "string" ||
      !value.attemptId ||
      !["onboarding", "admin"].includes(value.scene) ||
      !["PRIMARY", "BACKUP"].includes(value.oauthAppId) ||
      !Number.isFinite(value.createdAt) ||
      value.createdAt > now ||
      now - value.createdAt > TTL ||
      (value.shop !== undefined &&
        (typeof value.shop !== "string" || !SHOP_PATTERN.test(value.shop)))
    )
      return null;
    return value as PopupInstallIntent;
  } catch {
    return null;
  }
}

/** Seed the new same-origin popup before leaving for Shopify. Never use window.name. */
export function bindPopupInstallWindow(storage: Storage, attemptId: string): void {
  if (!attemptId) throw new Error("Missing popup attempt");
  // window.open may clone an older onboarding intent from its opener.
  clearOnboardingInstall(storage);
  storage.setItem(WINDOW_ATTEMPT_KEY, attemptId);
}

export function readPopupInstallForWindow(
  storage: Storage,
  windowStorage: Storage,
  scope: { accountId: string; productCode: string },
  now = Date.now(),
): PopupInstallIntent | null {
  try {
    const attemptId = windowStorage.getItem(WINDOW_ATTEMPT_KEY);
    return attemptId ? readPopupInstall(storage, { ...scope, attemptId }, now) : null;
  } catch {
    return null;
  }
}

/** The popup records the resolved shop before leaving for Shopify OAuth. */
export function recordPopupInstallShop(
  storage: Storage,
  intent: PopupInstallIntent,
  shop: string,
): void {
  if (!SHOP_PATTERN.test(shop)) throw new Error("Invalid Shopify shop");
  const current = readPopupInstall(storage, intent);
  if (
    !current ||
    current.workspaceId !== intent.workspaceId ||
    current.oauthAppId !== intent.oauthAppId ||
    current.createdAt !== intent.createdAt ||
    (current.shop !== undefined && current.shop !== shop)
  )
    throw new Error("Shopify popup attempt expired");
  storage.setItem(
    `${STORAGE_KEY}:${intent.attemptId}`,
    JSON.stringify({ ...current, shop }),
  );
}

export function clearPopupInstall(storage: Storage, attemptId?: string): void {
  if (!attemptId) return;
  try {
    storage.removeItem(`${STORAGE_KEY}:${attemptId}`);
  } catch {}
}

/** Accept only Shopify results correlated with this exact attempt and workspace. */
export function parsePopupInstallMessage(
  data: unknown,
  correlation: { attemptId: string; workspaceId: string },
): PopupInstallResult | null {
  if (!data || typeof data !== "object") return null;
  const message = data as Record<string, unknown>;
  if (
    message.provider !== "shopify" ||
    message.attemptId !== correlation.attemptId ||
    message.workspaceId !== correlation.workspaceId
  )
    return null;
  if (message.type === "SUCCESS")
    return {
      status: "success",
      shop:
        typeof message.shop === "string" && SHOP_PATTERN.test(message.shop)
          ? message.shop
          : undefined,
    };
  if (message.type === "ERROR") return { status: "error" };
  return null;
}

export function postPopupInstallResult(payload: Record<string, unknown>): void {
  try {
    const channel = new BroadcastChannel(SHOPIFY_CONNECT_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  } catch {}
  try {
    window.opener?.postMessage(payload, window.location.origin);
  } catch {}
}

/** Same search the full-page OAuth return would carry, so existing verification owns completion. */
export function buildPopupInstallReturnSearch(intent: {
  workspaceId: string;
  attemptId: string;
  scene: "onboarding" | "admin";
}): string {
  const params = new URLSearchParams();
  params.set("connect_success", "shopify");
  params.set("workspace_id", intent.workspaceId);
  params.set(SHOPIFY_CONNECT_ATTEMPT_KEY, intent.attemptId);
  params.set(ONBOARDING_RETURN_KEY, "1");
  params.set("shopify_connection_scene", intent.scene);
  return `?${params}`;
}

export function listActiveShopifyDomains(
  value: unknown,
  oauthAppId: "PRIMARY" | "BACKUP",
): string[] {
  const rows = (value as { shopify?: unknown } | null)?.shopify;
  if (!Array.isArray(rows)) return [];
  const domains = new Set<string>();
  for (const row of rows as Array<Record<string, any>>) {
    if (row?.connected !== true || !Array.isArray(row.mappings)) continue;
    for (const mapping of row.mappings)
      if (
        mapping?.oauth_app_id === oauthAppId &&
        mapping?.status === "ACTIVE" &&
        typeof mapping.shop_domain === "string" &&
        SHOP_PATTERN.test(mapping.shop_domain)
      )
        domains.add(mapping.shop_domain);
  }
  return [...domains];
}

export function isValidShopifyAuthorizationUrl(raw: string, shop: string): boolean {
  try {
    const url = new URL(raw);
    return (
      url.protocol === "https:" &&
      url.hostname === shop &&
      !url.port &&
      !url.username &&
      !url.password &&
      url.pathname === "/admin/oauth/authorize"
    );
  } catch {
    return false;
  }
}
